import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { normalizeComplianceIntegration, complianceHealthPresentation } from "../lib/complianceIntegrationHealth.js";
import { denialCopy } from "../lib/denialReason.js";
import { PERMISSIONS } from "../lib/permissionKeys.js";
import { useMyPermissions } from "../lib/permissions.js";
import { restGet } from "../lib/restClient.js";

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;
const ComplianceIntegrationHealthContext = createContext(null);

function canReadComplianceHealth(caps) {
    if (!caps || caps.loading || caps.degraded || caps.subjectScopeDegraded) return false;
    if (!caps.available) return true;
    return caps.permissions instanceof Set
        ? caps.permissions.has(PERMISSIONS.USERS_MANAGE)
        : Array.isArray(caps.permissions) && caps.permissions.includes(PERMISSIONS.USERS_MANAGE);
}

function bannerCondition(integration) {
    if (!integration?.configured) return null;
    if (integration.healthStatus !== "degraded" && integration.healthStatus !== "action_required") return null;
    return `${integration.healthStatus}:${integration.healthErrorCode}`;
}

function OwnedComplianceIntegrationHealthProvider({ children }) {
    const caps = useMyPermissions();
    const authorized = canReadComplianceHealth(caps);
    const [state, setState] = useState({
        requestStatus: "idle",
        requestError: null,
        integration: null,
    });
    const [dismissedCondition, setDismissedCondition] = useState(null);
    const inflight = useRef(null);
    const mounted = useRef(true);
    const capsRef = useRef(caps);
    capsRef.current = caps;

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const refresh = useCallback(({ afterCurrent = false } = {}) => {
        if (!authorized) return Promise.resolve(null);
        if (inflight.current) {
            return afterCurrent
                ? inflight.current.then(() => refresh())
                : inflight.current;
        }

        setState((current) => ({
            ...current,
            requestStatus: current.integration ? "refreshing" : "loading",
            requestError: null,
        }));
        const request = restGet("/v1/compliance-integration")
            .then((response) => {
                const integration = normalizeComplianceIntegration(response);
                if (mounted.current) {
                    // Checks and failure counts can change without a new health
                    // condition. Only a changed condition or observed recovery
                    // clears this browsing session's dismissal.
                    setDismissedCondition((current) => current === bannerCondition(integration) ? current : null);
                    setState({ requestStatus: "ready", requestError: null, integration });
                }
                return integration;
            })
            .catch((error) => {
                if (mounted.current) {
                    setState((current) => ({
                        ...current,
                        requestStatus: "unavailable",
                        requestError: denialCopy(error, capsRef.current).message
                            || "Integration status is temporarily unavailable.",
                    }));
                }
                return null;
            })
            .finally(() => {
                if (inflight.current === request) inflight.current = null;
            });
        inflight.current = request;
        return request;
    }, [authorized]);

    useEffect(() => {
        if (!authorized) {
            setDismissedCondition(null);
            setState({ requestStatus: "idle", requestError: null, integration: null });
            return undefined;
        }

        refresh();
        const refreshWhenVisible = () => {
            if (typeof document === "undefined" || document.visibilityState === "visible") refresh();
        };
        const interval = globalThis.setInterval(refreshWhenVisible, REFRESH_INTERVAL_MS);
        globalThis.window?.addEventListener("focus", refreshWhenVisible);
        globalThis.document?.addEventListener("visibilitychange", refreshWhenVisible);
        return () => {
            globalThis.clearInterval(interval);
            globalThis.window?.removeEventListener("focus", refreshWhenVisible);
            globalThis.document?.removeEventListener("visibilitychange", refreshWhenVisible);
        };
    }, [authorized, refresh]);

    const condition = authorized ? bannerCondition(state.integration) : null;
    const bannerDismissed = condition !== null && dismissedCondition === condition;
    const dismissBanner = useCallback(() => {
        if (condition !== null) setDismissedCondition(condition);
    }, [condition]);
    const value = useMemo(() => ({ ...state, authorized, refresh, bannerDismissed, dismissBanner }),
        [state, authorized, refresh, bannerDismissed, dismissBanner]);
    return (
        <ComplianceIntegrationHealthContext.Provider value={value}>
            {children}
        </ComplianceIntegrationHealthContext.Provider>
    );
}

export function ComplianceIntegrationHealthProvider({ children }) {
    const inherited = useContext(ComplianceIntegrationHealthContext);
    if (inherited) return children;
    return <OwnedComplianceIntegrationHealthProvider>{children}</OwnedComplianceIntegrationHealthProvider>;
}

export function useOptionalComplianceIntegrationHealth() {
    return useContext(ComplianceIntegrationHealthContext);
}

export function useComplianceIntegrationHealth() {
    const value = useOptionalComplianceIntegrationHealth();
    if (!value) {
        throw new Error("useComplianceIntegrationHealth requires ComplianceIntegrationHealthProvider");
    }
    return value;
}

export function ComplianceIntegrationHealthBanner({ onOpenDataSources }) {
    const { authorized, requestStatus, integration, bannerDismissed, dismissBanner } = useComplianceIntegrationHealth();
    if (!authorized || bannerDismissed || !bannerCondition(integration)) return null;

    const presentation = complianceHealthPresentation(integration);
    const critical = integration.healthStatus === "action_required";
    return (
        <div
            role="alert"
            data-compliance-health-banner
            className={`mx-[var(--main-inset)] my-[8px] flex flex-wrap items-start justify-between gap-[12px] border px-[10px] py-[8px] text-[11px] leading-[1.4] ${
                critical
                    ? "border-red-500/50 bg-red-500/10 text-[color:var(--ink)]"
                    : "border-amber-500/50 bg-amber-500/10 text-[color:var(--ink)]"
            }`}
        >
            <span className="min-w-0 flex-[1_1_240px]">
                <strong className="font-medium">Anthropic capture: {presentation.label}.</strong>{" "}
                {presentation.impact}
                {requestStatus === "unavailable" ? " The latest status refresh is temporarily unavailable." : ""}
            </span>
            <div className="flex shrink-0 flex-wrap gap-[8px]">
                <button
                    type="button"
                    onClick={onOpenDataSources}
                    className="border border-[color:var(--line)] bg-[color:var(--bg)] px-[8px] py-[3px] text-[10px] text-[color:var(--ink)]"
                >
                    Open Data Sources
                </button>
                <button
                    type="button"
                    onClick={dismissBanner}
                    aria-label="Dismiss Anthropic capture alert"
                    className="border border-[color:var(--line)] bg-[color:var(--bg)] px-[8px] py-[3px] text-[10px] text-[color:var(--ink)]"
                >
                    Dismiss
                </button>
            </div>
        </div>
    );
}
